import {
  Button,
  Flex,
  Heading,
  Icon,
  IconButton,
  Tag,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { IngestionFolderValidationErrorReviewFragment } from '@client/graphql/__generated__/types';
import type { LucideIcon } from 'lucide-react';
import { FileX, Plus, TriangleAlertIcon, X } from 'lucide-react';
import { FC, useMemo, useState } from 'react';
import { ComplianceValidationVariant } from './ComplianceDrawer';
import {
  AddIssueModal,
  IssueFormData,
  IssueType,
} from './components/AddIssueModal';
import { HideIssueModal, HideIssueType } from './components/HideIssueModal';
import {
  FolderValidation,
  getMissingDocumentsFromFolderValidation,
  getOfferRuleItemsFromFolderValidation,
  isMissingDocumentHidden,
  isOfferRuleItemHidden,
} from './utils/folderIssues.utils';

interface FolderIssuesCardProps {
  variant: ComplianceValidationVariant;
  folderValidation?: FolderValidation;
  userAddedIssues?: IngestionFolderValidationErrorReviewFragment[];
  hiddenIssues?: IngestionFolderValidationErrorReviewFragment[];
  onAddFolderIssue?: (issueData: IssueFormData) => void | Promise<void>;
  onDeleteFolderIssue?: (reviewId: string) => void | Promise<void>;
  onHideFolderIssue?: (
    isHidden: boolean,
    message?: string,
    documentCategory?: string,
    documentForm?: string,
    documentName?: string,
    ruleUuid?: string
  ) => void | Promise<void>;
}

type OfferRuleDisplayItem = {
  id: string;
  message: string;
  documentCategory?: string;
  documentForm?: string;
  documentName?: string;
  ruleUuid?: string;
  isHidden: boolean;
};

type FolderIssueRowProps = {
  icon: LucideIcon;
  isHidden: boolean;
  primaryText: string;
  secondaryText?: string;
  tooltipLabel?: string;
  showActionButton: boolean;
  actionAriaLabel: string;
  onAction?: () => void | Promise<void>;
  cursor?: 'default' | 'pointer';
};

const FolderIssueRow: FC<FolderIssueRowProps> = ({
  icon,
  isHidden,
  primaryText,
  secondaryText,
  tooltipLabel,
  showActionButton,
  actionAriaLabel,
  onAction,
  cursor = 'default',
}) => (
  <Flex
    align="center"
    cursor={cursor}
    gap={2}
    justify="space-between"
    opacity={isHidden ? 0.6 : 1}
    px={2}
  >
    <Flex align="center" gap={2} overflow="hidden">
      <Icon
        as={icon}
        color={isHidden ? 'compliance.text.muted' : 'compliance.status.error'}
        size={14}
      />
      <Flex direction="column" gap={0} overflow="hidden">
        {secondaryText && (
          <Text
            color="compliance.text.muted"
            fontSize="xs"
            textDecoration={isHidden ? 'line-through' : undefined}
          >
            {secondaryText}
          </Text>
        )}
        <Tooltip
          bg="compliance.tooltip.bg"
          borderColor="compliance.tooltip.border"
          borderWidth={1}
          color="compliance.tooltip.text"
          label={tooltipLabel ?? primaryText}
        >
          <Text
            color={isHidden ? 'compliance.text.muted' : undefined}
            fontSize="sm"
            fontWeight="medium"
            overflow="hidden"
            textDecoration={isHidden ? 'line-through' : undefined}
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {primaryText}
          </Text>
        </Tooltip>
      </Flex>
    </Flex>

    {showActionButton && onAction && (
      <IconButton
        aria-label={actionAriaLabel}
        color="compliance.text.muted"
        icon={<Icon as={X} />}
        size="xxs"
        variant="link"
        onClick={() => {
          void onAction();
        }}
      />
    )}
  </Flex>
);

export const FolderIssuesCard: FC<FolderIssuesCardProps> = ({
  variant,
  folderValidation,
  hiddenIssues,
  onAddFolderIssue,
  onDeleteFolderIssue,
  onHideFolderIssue,
  userAddedIssues,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHideModalOpen, setIsHideModalOpen] = useState(false);
  const [selectedIssueToHide, setSelectedIssueToHide] = useState<{
    type: 'document' | 'offerRule';
    documentCategory?: string;
    documentForm?: string;
    documentName?: string;
    ruleUuid?: string | null;
    issueMessage?: string;
  } | null>(null);

  // Get missing documents from folder validation
  const missingDocuments = useMemo(
    () => getMissingDocumentsFromFolderValidation(folderValidation),
    [folderValidation]
  );

  const offerRuleItems = useMemo(() => {
    const items = getOfferRuleItemsFromFolderValidation(folderValidation);

    return items.map<OfferRuleDisplayItem>((item) => {
      const ruleUuid = item.ruleUuid ?? undefined;

      return {
        id: item.sourceId,
        message: item.message,
        documentCategory: item.documentCategory,
        documentForm: item.documentForm,
        documentName: item.documentName,
        ruleUuid,
        isHidden: isOfferRuleItemHidden(item, hiddenIssues),
      };
    });
  }, [folderValidation, hiddenIssues]);

  const handleAddIssue = async (issueData: IssueFormData) => {
    if (onAddFolderIssue) {
      await onAddFolderIssue(issueData);
    }
  };

  const handleDeleteIssue = async (reviewId: string) => {
    if (onDeleteFolderIssue) {
      await onDeleteFolderIssue(reviewId);
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleOpenHideModal = (issue: {
    type: 'document' | 'offerRule';
    documentCategory?: string;
    documentForm?: string;
    documentName?: string;
    ruleUuid?: string | null;
    issueMessage?: string;
  }) => {
    setSelectedIssueToHide(issue);
    setIsHideModalOpen(true);
  };

  const handleCloseHideModal = () => {
    setIsHideModalOpen(false);
    setSelectedIssueToHide(null);
  };

  const handleHideIssue = async (message: string) => {
    if (onHideFolderIssue && selectedIssueToHide) {
      const payload = selectedIssueToHide;

      if (payload.type === 'document') {
        await onHideFolderIssue(
          true,
          message,
          payload.documentCategory,
          payload.documentForm,
          payload.documentName,
          undefined
        );
      } else {
        const messageToSend =
          payload.ruleUuid && payload.ruleUuid !== ''
            ? message
            : payload.issueMessage ?? message;

        await onHideFolderIssue(
          true,
          messageToSend,
          payload.documentCategory,
          payload.documentForm,
          payload.documentName,
          payload.ruleUuid ?? undefined
        );
      }
    }
  };

  const folderIssueCount =
    (missingDocuments?.length || 0) +
    (userAddedIssues?.length || 0) +
    (offerRuleItems.length || 0);

  const hasMissingDocuments = missingDocuments.length > 0;
  const hasOfferRuleItems = offerRuleItems.length > 0;
  const hasUserAddedIssues = (userAddedIssues?.length || 0) > 0;

  return (
    <>
      <Flex direction="column" gap={3}>
        <Flex align="center" justify="space-between">
          <Heading color="compliance.text.secondary" size="smallUppercase">
            Folder issues {folderIssueCount > 0 && `(${folderIssueCount})`}
          </Heading>
          {variant === 'external' && onAddFolderIssue && (
            <Button
              bg="compliance.button.secondary.bg"
              color="compliance.button.secondary.text"
              height="24px"
              leftIcon={<Icon as={Plus} />}
              p={1}
              size="xs"
              onClick={handleOpenModal}
            >
              Add
            </Button>
          )}
        </Flex>

        {/* User Added Issues Section */}
        {userAddedIssues && userAddedIssues.length > 0 && (
          <VStack align="stretch" spacing={2}>
            {userAddedIssues.map((issue) => {
              const isHidden = issue.isHidden;

              return (
                <Flex
                  key={issue.id}
                  align="center"
                  cursor={onDeleteFolderIssue ? 'pointer' : 'default'}
                  gap={2}
                  justify="space-between"
                  opacity={isHidden ? 0.6 : 1}
                  px={2}
                >
                  <Flex align="center" gap={2} overflow="hidden">
                    <Tag
                      borderRadius="xs"
                      color={isHidden ? 'compliance.text.muted' : undefined}
                      size="sm"
                    >
                      <Text fontSize="xs">Custom</Text>
                    </Tag>
                    <Tooltip
                      bg="compliance.tooltip.bg"
                      borderColor="compliance.tooltip.border"
                      borderWidth={1}
                      color="compliance.tooltip.text"
                      label={issue.message || 'User added issue'}
                    >
                      <Text
                        color={isHidden ? 'compliance.text.muted' : undefined}
                        fontSize="sm"
                        fontWeight="medium"
                        overflow="hidden"
                        textDecoration={isHidden ? 'line-through' : undefined}
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                      >
                        {issue.message || 'User added issue'}
                      </Text>
                    </Tooltip>
                  </Flex>
                  {variant === 'external' && onDeleteFolderIssue && (
                    <IconButton
                      aria-label="Delete issue"
                      color="compliance.text.muted"
                      icon={<Icon as={X} />}
                      size="xxs"
                      variant="link"
                      onClick={() => void handleDeleteIssue(issue.id)}
                    />
                  )}
                </Flex>
              );
            })}
          </VStack>
        )}

        {/* Missing Documents + Offer Rule Issues Section */}
        {hasMissingDocuments || hasOfferRuleItems ? (
          <VStack align="stretch" spacing={2}>
            {missingDocuments.map((document, index) => {
              const rowKey = `missing-${index}`;
              const isHidden = isMissingDocumentHidden(document, hiddenIssues);
              const showActions = variant === 'external' && !!onHideFolderIssue;

              const handleMissingDocAction = () => {
                if (!onHideFolderIssue) {
                  return;
                }

                if (isHidden) {
                  void onHideFolderIssue(
                    false,
                    undefined,
                    document.documentCategory,
                    document.documentForm,
                    document.documentName,
                    undefined
                  );
                } else {
                  handleOpenHideModal({
                    type: 'document',
                    documentCategory: document.documentCategory,
                    documentForm: document.documentForm,
                    documentName: document.documentName,
                    ruleUuid: null,
                    issueMessage: undefined,
                  });
                }
              };

              return (
                <FolderIssueRow
                  key={rowKey}
                  actionAriaLabel={isHidden ? 'Unhide issue' : 'Hide issue'}
                  cursor={showActions ? 'pointer' : 'default'}
                  icon={FileX}
                  isHidden={isHidden}
                  primaryText={document.mainLabel}
                  secondaryText={document.subLabel}
                  showActionButton={showActions}
                  tooltipLabel={`Missing doc: ${document.mainLabel}`}
                  onAction={showActions ? handleMissingDocAction : undefined}
                />
              );
            })}
            {offerRuleItems.map((item) => {
              const showActions = variant === 'external' && !!onHideFolderIssue;

              const handleOfferRuleAction = () => {
                if (!onHideFolderIssue) {
                  return;
                }

                if (!item.ruleUuid) {
                  return;
                }

                if (item.isHidden) {
                  void onHideFolderIssue(
                    false,
                    undefined,
                    item.documentCategory,
                    item.documentForm,
                    item.documentName,
                    item.ruleUuid
                  );

                  return;
                }

                handleOpenHideModal({
                  type: 'offerRule',
                  documentCategory: item.documentCategory,
                  documentForm: item.documentForm,
                  documentName: item.documentName,
                  ruleUuid: item.ruleUuid,
                  issueMessage: item.message,
                });
              };

              return (
                <FolderIssueRow
                  key={item.id}
                  actionAriaLabel={
                    item.isHidden ? 'Unhide issue' : 'Hide issue'
                  }
                  cursor={showActions ? 'pointer' : 'default'}
                  icon={TriangleAlertIcon}
                  isHidden={item.isHidden}
                  primaryText={item.message}
                  showActionButton={showActions}
                  tooltipLabel={item.message}
                  onAction={showActions ? handleOfferRuleAction : undefined}
                />
              );
            })}
          </VStack>
        ) : !hasUserAddedIssues ? (
          <Text color="compliance.text.muted" fontSize="sm" px={2}>
            No issues
          </Text>
        ) : null}
      </Flex>

      {variant === 'external' && onAddFolderIssue && (
        <AddIssueModal
          isOpen={isModalOpen}
          issueType={IssueType.FOLDER}
          onClose={handleCloseModal}
          onSubmit={handleAddIssue}
        />
      )}

      {variant === 'external' && onHideFolderIssue && (
        <HideIssueModal
          isOpen={isHideModalOpen}
          issueType={HideIssueType.FOLDER}
          onClose={handleCloseHideModal}
          onSubmit={handleHideIssue}
        />
      )}
    </>
  );
};
